import React, { useState, useEffect } from 'react';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI } from '../lib/api';
import '../App.css';

export default function Invoices() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showPdfView, setShowPdfView] = useState(false);

    useEffect(() => {
        fetchInvoices();
    }, [filter]);

    const fetchInvoices = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await customerAPI.getInvoices(params);
            setInvoices(response.data.invoices || []);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError(err.response?.data?.error || 'Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    const viewInvoice = async (invoice) => {
        try {
            setSelectedInvoice(invoice);
            setShowPdfView(false);
            // Fetch PDF blob
            fetchPdfBlob(invoice.invoice_id);
        } catch (err) {
            console.error('Error fetching invoice details:', err);
            alert('Failed to load invoice details');
        }
    };

    const fetchPdfBlob = async (invoiceId) => {
        setPdfLoading(true);
        try {
            // Revoke old blob URL if it exists
            if (pdfBlob && typeof pdfBlob === 'string' && pdfBlob.startsWith('blob:')) {
                URL.revokeObjectURL(pdfBlob);
            }
            
            const token = localStorage.getItem('customerToken');
            const apiUrl = process.env.REACT_APP_API_URL || 'https://rmm-psa-backend-t9f7k.ondigitalocean.app';
            const response = await fetch(`${apiUrl}/api/portal/customer/invoices/${invoiceId}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch PDF');
            }
            
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPdfBlob(blobUrl);
        } catch (err) {
            console.error('Error fetching PDF:', err);
        } finally {
            setPdfLoading(false);
        }
    };

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (pdfBlob && typeof pdfBlob === 'string' && pdfBlob.startsWith('blob:')) {
                URL.revokeObjectURL(pdfBlob);
            }
        };
    }, [pdfBlob]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU');
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return '#28a745';
            case 'unpaid': return '#ffc107';
            case 'overdue': return '#dc3545';
            case 'pending': return '#17a2b8';
            case 'draft': return '#6c757d';
            case 'cancelled': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return 'Paid';
            case 'unpaid': return 'Unpaid';
            case 'overdue': return 'Overdue';
            case 'pending': return 'Awaiting Payment';
            case 'draft': return 'Draft';
            case 'cancelled': return 'Cancelled';
            default: return status || 'Unknown';
        }
    };

    const downloadPDF = async (invoiceId, invoiceNumber) => {
        try {
            const token = localStorage.getItem('customerToken');
            const apiUrl = process.env.REACT_APP_API_URL || 'https://rmm-psa-backend-t9f7k.ondigitalocean.app';
            
            // Fetch PDF with authorization header
            const response = await fetch(`${apiUrl}/api/portal/customer/invoices/${invoiceId}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch PDF');
            }
            
            // Create blob and download
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${invoiceNumber || `invoice-${invoiceId}`}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading PDF:', err);
            alert('Failed to download PDF');
        }
    };

    return (
        <CustomerLayout>
            <div style={{ maxWidth: '1200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h1>Invoices</h1>
                        <p style={{ color: '#666', marginTop: '8px' }}>View and manage your invoices</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <select
                            className="form-input"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            style={{ width: 'auto' }}
                        >
                            <option value="all">All Invoices</option>
                            <option value="Paid">Paid</option>
                            <option value="Unpaid">Unpaid</option>
                            <option value="Overdue">Overdue</option>
                        </select>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error">{error}</div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner"></div>
                        <p style={{ marginTop: '20px', color: '#666' }}>Loading invoices...</p>
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
                        <p style={{ fontSize: '18px', color: '#666' }}>No invoices found</p>
                    </div>
                ) : (
                    <div className="card">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Invoice #</th>
                                    <th>Date</th>
                                    <th>Due Date</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.invoice_id}>
                                        <td><strong>{invoice.invoice_number}</strong></td>
                                        <td>{formatDate(invoice.invoice_date)}</td>
                                        <td>{formatDate(invoice.due_date)}</td>
                                        <td><strong>{formatCurrency(invoice.total)}</strong></td>
                                        <td>
                                            <span
                                                className="status-badge"
                                                style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500',
                                                    backgroundColor: getStatusColor(invoice.status),
                                                    color: 'white'
                                                }}
                                            >
                                                {getStatusLabel(invoice.status)}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <button
                                                    onClick={() => viewInvoice(invoice)}
                                                    style={{
                                                        padding: '6px 16px',
                                                        backgroundColor: '#1976d2',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    View Details
                                                </button>
                                                {(invoice.status === 'unpaid' || invoice.status === 'overdue') && invoice.payment_token && (
                                                    <button
                                                        onClick={() => {
                                                            window.location.href = `https://everydaytech.au/pay/${invoice.payment_token}`;
                                                        }}
                                                        style={{
                                                            padding: '6px 16px',
                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontWeight: '600',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        💳 Pay Now
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Invoice Detail Modal */}
            {selectedInvoice && (
                <div 
                    style={{ 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        backgroundColor: 'rgba(0,0,0,0.5)', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        zIndex: 1000 
                    }}
                    onClick={() => setSelectedInvoice(null)}
                >
                    <div 
                        style={{ 
                            backgroundColor: 'white', 
                            borderRadius: '8px', 
                            maxWidth: '1000px',
                                width: '95%',
                                maxHeight: '95vh',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '20px 30px',
                                borderBottom: '1px solid #e0e0e0'
                            }}>
                                <div>
                                    <h2 style={{ margin: 0 }}>Invoice {selectedInvoice.invoice_number}</h2>
                                    <p style={{ color: '#666', margin: '5px 0 0 0' }}>{formatDate(selectedInvoice.invoice_date)}</p>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    {/* Toggle View Buttons */}
                                    <button
                                        className={`btn btn-sm ${!showPdfView ? 'btn-primary' : 'btn-secondary'}`}
                                        onClick={() => setShowPdfView(false)}
                                        style={{ fontSize: '12px', padding: '6px 12px' }}
                                    >
                                        📋 Details
                                    </button>
                                    <button
                                        className={`btn btn-sm ${showPdfView ? 'btn-primary' : 'btn-secondary'}`}
                                        onClick={() => setShowPdfView(true)}
                                        style={{ fontSize: '12px', padding: '6px 12px' }}
                                    >
                                        📄 PDF
                                    </button>
                                    <button
                                        className="btn btn-sm"
                                        onClick={() => setSelectedInvoice(null)}
                                        style={{ fontSize: '12px', padding: '6px 12px' }}
                                    >
                                        ✕ Close
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div style={{ 
                                padding: '30px',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                {showPdfView ? (
                                    // PDF Viewer
                                    <div style={{ 
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '600px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {pdfLoading ? (
                                            <div style={{ textAlign: 'center' }}>
                                                <div className="spinner"></div>
                                                <p style={{ marginTop: '20px', color: '#666' }}>Loading PDF...</p>
                                            </div>
                                        ) : pdfBlob ? (
                                            <iframe
                                                src={pdfBlob}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    minHeight: '600px',
                                                    border: 'none'
                                                }}
                                                title="Invoice PDF"
                                            />
                                        ) : (
                                            <div style={{ textAlign: 'center' }}>
                                                <p style={{ color: '#999' }}>Failed to load PDF</p>
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => fetchPdfBlob(selectedInvoice.invoice_id)}
                                                    style={{ marginTop: '10px' }}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Details View
                                    <>
                                        {selectedInvoice.notes && (
                                            <div style={{ 
                                                padding: '15px',
                                                background: '#f8f9fa',
                                                borderRadius: '4px',
                                                marginBottom: '20px',
                                                fontStyle: 'italic',
                                                color: '#666'
                                            }}>
                                                {selectedInvoice.notes}
                                            </div>
                                        )}

                                        <div style={{ marginBottom: '30px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <h4 style={{ marginBottom: '10px', color: '#666' }}>Due Date</h4>
                                                    <p>{formatDate(selectedInvoice.due_date)}</p>
                                                </div>
                                                <div>
                                                    <h4 style={{ marginBottom: '10px', color: '#666' }}>Status</h4>
                                                    <span
                                                        className="status-badge"
                                                        style={{ backgroundColor: getStatusColor(selectedInvoice.status), color: 'white' }}
                                                    >
                                                        {getStatusLabel(selectedInvoice.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {selectedInvoice.line_items && selectedInvoice.line_items.length > 0 && (
                                            <div style={{ marginBottom: '30px' }}>
                                                <h4 style={{ marginBottom: '15px' }}>Line Items</h4>
                                                <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th>Description</th>
                                                            <th>Quantity</th>
                                                            <th>Rate</th>
                                                            <th>Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                            {selectedInvoice.line_items.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.description}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{formatCurrency(item.rate)}</td>
                                                    <td>{formatCurrency(item.amount)}</td>
                                                </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        <div style={{
                                            borderTop: '2px solid #ddd',
                                            paddingTop: '20px',
                                            textAlign: 'right'
                                        }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                <span style={{ marginRight: '20px', color: '#666' }}>Subtotal:</span>
                                                <strong>{formatCurrency(selectedInvoice.subtotal)}</strong>
                                            </div>
                                            <div style={{ marginBottom: '10px' }}>
                                                <span style={{ marginRight: '20px', color: '#666' }}>Tax (GST):</span>
                                                <strong>{formatCurrency(selectedInvoice.tax || selectedInvoice.tax_amount || selectedInvoice.tax_total || 0)}</strong>
                                            </div>
                                            <div style={{ fontSize: '20px', marginTop: '15px' }}>
                                                <span style={{ marginRight: '20px' }}>Total:</span>
                                                <strong style={{ color: '#007bff' }}>{formatCurrency(selectedInvoice.total)}</strong>
                                            </div>
                                        </div>

                                        {selectedInvoice.payment_date && (
                                            <div style={{
                                                marginTop: '20px',
                                                padding: '15px',
                                                background: '#d4edda',
                                                borderRadius: '4px'
                                            }}>
                                                <strong style={{ color: '#155724' }}>
                                                    Paid on {formatDate(selectedInvoice.payment_date)}
                                                    {selectedInvoice.payment_method && ` via ${selectedInvoice.payment_method}`}
                                                </strong>
                                            </div>
                                        )}

                                        {/* Payment Options - Show only for unpaid invoices */}
                                        {(selectedInvoice.status === 'unpaid' || selectedInvoice.status === 'overdue' || !selectedInvoice.status) && (
                                            <div style={{
                                                marginTop: '30px',
                                                padding: '20px',
                                                background: '#f8f9fa',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                <h4 style={{ marginBottom: '20px', fontSize: '18px' }}>Payment Options</h4>
                                                
                                                {/* Online Payment */}
                                                <div style={{
                                                    padding: '15px',
                                                    background: 'white',
                                                    borderRadius: '6px',
                                                    marginBottom: '15px',
                                                    border: '1px solid #dee2e6'
                                                }}>
                                                    <h5 style={{ marginBottom: '10px', fontSize: '16px' }}>💳 Option 1: Pay Online (Credit/Debit Card)</h5>
                                                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
                                                        Click the "Pay Now" button below to pay securely with credit card via Stripe.
                                                    </p>
                                                </div>

                                                {/* Bank Transfer */}
                                                <div style={{
                                                    padding: '15px',
                                                    background: 'white',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dee2e6'
                                                }}>
                                                    <h5 style={{ marginBottom: '10px', fontSize: '16px' }}>🏦 Option 2: Direct Bank Transfer</h5>
                                                    <table style={{ 
                                                        width: '100%', 
                                                        fontSize: '14px',
                                                        lineHeight: '1.8',
                                                        marginBottom: '10px'
                                                    }}>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', color: '#666' }}><strong>Bank Name:</strong></td>
                                                                <td style={{ padding: '4px 0' }}>Commonwealth Bank</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', color: '#666' }}><strong>Account Name:</strong></td>
                                                                <td style={{ padding: '4px 0' }}>Independent Business Group</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', color: '#666' }}><strong>BSB:</strong></td>
                                                                <td style={{ padding: '4px 0' }}>067-873</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', color: '#666' }}><strong>Account Number:</strong></td>
                                                                <td style={{ padding: '4px 0' }}>1145 4407</td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 0', color: '#666' }}><strong>Reference:</strong></td>
                                                                <td style={{ padding: '4px 0' }}>Invoice #{selectedInvoice.invoice_id}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                    <div style={{
                                                        padding: '10px',
                                                        background: '#fff3cd',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ffc107',
                                                        marginTop: '10px'
                                                    }}>
                                                        <p style={{ 
                                                            margin: 0, 
                                                            color: '#856404', 
                                                            fontSize: '13px',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            ⚠️ Please note: Bank transfers can take up to 7 business days to process and show on your account.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                            <button 
                                                className="btn btn-secondary"
                                                onClick={() => downloadPDF(selectedInvoice.invoice_id, selectedInvoice.invoice_number)}
                                            >
                                                💾 Download PDF
                                            </button>
                                            {(selectedInvoice.status === 'unpaid' || selectedInvoice.status === 'overdue' || !selectedInvoice.status) && selectedInvoice.payment_token && (
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => {
                                                        window.location.href = `https://everydaytech.au/pay/${selectedInvoice.payment_token}`;
                                                    }}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        border: 'none',
                                                        padding: '10px 24px',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    💳 Pay Now
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
        </CustomerLayout>
    );
}
